import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Crown } from "lucide-react";
import { Toaster, toast } from "sonner";
import api, { apiError } from "@/api";
import { LanguageProvider, useLang } from "@/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ConfigProvider } from "@/context/ConfigContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import StudentDashboard from "@/pages/student/StudentDashboard";
import PointsHistoryPage from "@/pages/student/PointsHistoryPage";
import LeaderboardPage from "@/pages/student/LeaderboardPage";
import ChallengesPage from "@/pages/student/ChallengesPage";
import BadgesPage from "@/pages/student/BadgesPage";
import ProfilePage from "@/pages/student/ProfilePage";
import TeacherDashboard from "@/pages/teacher/TeacherDashboard";
import StudentsPage from "@/pages/teacher/StudentsPage";
import TeacherChallengesPage from "@/pages/teacher/TeacherChallengesPage";
import AdminPage from "@/pages/admin/AdminPage";

const HOME_BY_ROLE = { admin: "/admin", super_admin: "/admin", teacher: "/teacher", student: "/" };

function Splash() {
  return (
    <div className="min-h-screen bg-[#08082C] flex items-center justify-center" data-testid="splash">
      <Crown className="w-14 h-14 text-[#FFC83D] animate-twinkle" fill="currentColor" />
    </div>
  );
}

function ForceChangePassword() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [cur, setCur] = React.useState("");
  const [next, setNext] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if (next.length < 6) { toast.error(t("validation.min_6")); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success(t("auth.password_changed"));
      setUser({ ...user, must_change_password: false });
    } catch (e) {
      toast.error(apiError(e, t));
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#08082C] bg-stardust flex items-center justify-center p-4">
      <div className="magic-panel w-full max-w-md p-8 animate-pop" data-testid="force-change-password">
        <h2 className="text-xl font-black text-center mb-2">{t("auth.change_password")}</h2>
        <p className="text-sm text-[#A5A0D6] font-bold text-center mb-5">{t("auth.must_change")}</p>
        <div className="space-y-3">
          <input data-testid="force-current-password" type="password" className="input-magic" dir="ltr"
            placeholder={t("auth.current_password")} value={cur} onChange={(e) => setCur(e.target.value)} />
          <input data-testid="force-new-password" type="password" className="input-magic" dir="ltr"
            placeholder={t("auth.new_password")} value={next} onChange={(e) => setNext(e.target.value)} />
          <button data-testid="force-password-submit" disabled={busy || !cur || !next} onClick={submit} className="btn-magic w-full">
            {busy ? t("common.loading") : t("common.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForceChangePassword />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={HOME_BY_ROLE[user.role] || "/"} replace />;
  }
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user.role !== "student") return <Navigate to={HOME_BY_ROLE[user.role]} replace />;
  return <StudentDashboard />;
}

function AppShell() {
  const { isRTL } = useLang();
  return (
    <>
      <Toaster position="top-center" theme="dark" richColors dir={isRTL ? "rtl" : "ltr"}
        toastOptions={{ style: { background: "#171347", border: "1px solid rgba(255,255,255,0.14)", color: "#fff", fontFamily: "inherit" } }} />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<Protected><ConfigProvider><Layout /></ConfigProvider></Protected>}>
          <Route path="/" element={<RoleHome />} />
          <Route path="/ranking" element={<LeaderboardPage />} />
          <Route path="/challenges" element={<Protected roles={["student"]}><ChallengesPage /></Protected>} />
          <Route path="/badges" element={<Protected roles={["student"]}><BadgesPage /></Protected>} />
          <Route path="/history" element={<Protected roles={["student"]}><PointsHistoryPage /></Protected>} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/:id" element={<Protected roles={["teacher", "admin", "super_admin"]}><ProfilePage /></Protected>} />
          <Route path="/teacher" element={<Protected roles={["teacher", "admin", "super_admin"]}><TeacherDashboard /></Protected>} />
          <Route path="/teacher/students" element={<Protected roles={["teacher", "admin", "super_admin"]}><StudentsPage /></Protected>} />
          <Route path="/teacher/challenges" element={<Protected roles={["teacher", "admin", "super_admin"]}><TeacherChallengesPage /></Protected>} />
          <Route path="/admin" element={<Protected roles={["admin", "super_admin"]}><AdminPage /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppShell />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
